import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ReceiptText, Paperclip, FileDown, Trash, FileText, Wallet, AlertCircle, ReceiptIndianRupee } from "lucide-react";
import CrmPanel from "./CrmPanel";
import StatusPill from "./StatusPill";
import { RecordPaymentModal, coerceGst } from "./ProjectDetail";
import {
  adminListClients, apiErrorMsg,
  crmListNonProject, crmCreateNonProject, crmUpdateNonProject, crmDeleteNonProject,
  crmRecordNonProjectPayment, crmDeleteNonProjectPayment,
  crmUploadNonProjectFile, crmDeleteNonProjectFile, crmNonProjectFileUrl,
} from "@/lib/api";
import { INR, INR_PRECISE, fmtDate, INVOICE_STATUSES, INVOICE_TONE } from "./crmUtils";

const ENTITY = "non-project-invoices";
const refresh = () => window.dispatchEvent(new Event(`crm-${ENTITY}-refresh`));
const chip = "inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full";

function KpiCard({ icon, label, value, tone, accent = "text-[#0a0a0a]", testid }) {
  return (
    <div className="bg-white p-5 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">{icon} {label}</div>
      <div className={`mt-2 text-2xl md:text-3xl font-bold ${accent}`}>{value}</div>
      <span className={`absolute top-0 left-0 h-1 w-full ${tone}`} />
    </div>
  );
}

function FileCell({ row, token, onUpload, onRemove }) {
  if (!row.has_attachment) {
    return (
      <button onClick={() => onUpload(row)} data-testid={`np-invoice-upload-${row.id}`}
        className={`${chip} border border-black/15 hover:border-[#ff5722] hover:text-[#ff5722]`}>
        <Paperclip className="h-3.5 w-3.5" /> Upload
      </button>
    );
  }
  const view = () => fetch(crmNonProjectFileUrl(row.id), { headers: { "X-Admin-Token": token } })
    .then((res) => { if (!res.ok) throw new Error(); return res.blob(); })
    .then((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = row.attachment?.filename || `${row.invoice_number || "invoice"}.pdf`;
      a.click();
    })
    .catch(() => toast.error("Download failed"));
  return (
    <div className="inline-flex items-center gap-1">
      <button onClick={view} data-testid={`np-invoice-download-${row.id}`}
        className={`${chip} border border-[#ff5722]/30 text-[#ff5722] hover:bg-[#ff5722] hover:text-white`}>
        <FileDown className="h-3.5 w-3.5" /> View
      </button>
      <button onClick={() => onRemove(row)} title="Remove file" data-testid={`np-invoice-remove-file-${row.id}`}
        className="text-rose-500 hover:text-rose-700"><Trash className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function PaymentCell({ row, onOpen }) {
  if (row.balance <= 0.01 && row.paid_amount > 0) {
    return (
      <button onClick={() => onOpen(row)} data-testid={`np-invoice-settled-${row.id}`}
        className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-700 font-bold hover:underline">
        ✓ Settled
      </button>
    );
  }
  return (
    <button onClick={() => onOpen(row)} data-testid={`np-invoice-record-${row.id}`}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.15em] bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] transition-colors shadow-sm">
      <ReceiptText className="h-3.5 w-3.5" /> Record
    </button>
  );
}

function useFileActions(token) {
  const inputRef = useRef(null);
  const targetRef = useRef(null);
  const onUpload = (row) => { targetRef.current = row; inputRef.current?.click(); };
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const row = targetRef.current;
    if (!file || !row) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    try {
      await crmUploadNonProjectFile(token, row.id, file);
      toast.success(`Uploaded ${file.name}`);
      refresh();
    } catch (err) { toast.error(apiErrorMsg(err, "Upload failed")); }
  };
  const onRemove = async (row) => {
    if (!window.confirm(`Remove the file attached to ${row.invoice_number}?`)) return;
    try {
      await crmDeleteNonProjectFile(token, row.id);
      toast.success("File removed");
      refresh();
    } catch (err) { toast.error(apiErrorMsg(err, "Delete failed")); }
  };
  return { inputRef, onUpload, onFileChosen, onRemove };
}

export default function NonProjectPayments({ token }) {
  const [clients, setClients] = useState([]);
  const [totals, setTotals] = useState({ count: 0, invoiced: 0, received: 0, balance: 0 });
  const [recordingFor, setRecordingFor] = useState(null);
  const files = useFileActions(token);

  useEffect(() => { adminListClients(token).then(setClients).catch(() => {}); }, [token]);

  const clientOpts = useMemo(
    () => clients.map((c) => ({ id: c.id, label: c.name || c.dashboard_name || c.username })),
    [clients],
  );

  const fields = [
    { name: "client_id",      label: "Client",        type: "select", required: true,
      options: [{ value: "", label: "— Select client —" }, ...clientOpts.map((c) => ({ value: c.id, label: c.label }))] },
    { name: "invoice_number", label: "Invoice #",     type: "text", required: true, placeholder: "NP-2026-001" },
    { name: "work_title",     label: "Additional work", type: "text", required: true, full: true, placeholder: "e.g. Extra landing page, SEO audit, Logo refresh" },
    { name: "description",    label: "Description",   type: "textarea", full: true, rows: 2 },
    { name: "amount",         label: "Amount (₹)",    type: "money", required: true },
    { name: "gst_applicable", label: "GST",           type: "select",
      options: [{ value: "true", label: "GST" }, { value: "false", label: "Non-GST" }] },
    { name: "gst_pct",        label: "GST %",         type: "money", unit: "%", help: "Default 18%. Ignored if Non-GST." },
    { name: "status",         label: "Status",        type: "select", options: INVOICE_STATUSES.map((s) => ({ value: s.k, label: s.label })) },
    { name: "issued_date",    label: "Issued",        type: "date" },
    { name: "due_date",       label: "Due",           type: "date" },
    { name: "paid_date",      label: "Paid date",     type: "date", help: "Auto-filled when fully paid." },
    { name: "notes",          label: "Notes",         type: "textarea", full: true, rows: 3 },
  ];

  const columns = [
    { key: "invoice_number", label: "Invoice #", render: (r) => <span className="font-mono font-bold">{r.invoice_number}</span> },
    { key: "client_id",      label: "Client",    render: (r) => <span className="font-semibold text-[#0a0a0a]">{r.client_name || "—"}</span> },
    { key: "work_title",     label: "Work",      render: (r) => <span className="text-sm">{r.work_title}</span> },
    { key: "gst_applicable", label: "GST",       render: (r) => <StatusPill tone={r.gst_applicable ? "indigo" : "zinc"} label={r.gst_applicable ? `${r.gst_pct}%` : "None"} /> },
    { key: "total",          label: "Total",     render: (r) => <span className="font-mono font-bold">{INR_PRECISE(r.total ?? r.amount)}</span> },
    { key: "paid_amount",    label: "Paid",      render: (r) => <span className="font-mono text-emerald-700">{INR_PRECISE(r.paid_amount)}</span> },
    { key: "balance",        label: "Balance",   render: (r) => <span className={`font-mono ${r.balance > 0 ? "text-rose-700" : "text-[#4a4a4a]"}`}>{INR_PRECISE(r.balance)}</span> },
    { key: "record",         label: "Payment",   render: (r) => <PaymentCell row={r} onOpen={setRecordingFor} /> },
    { key: "issued_date",    label: "Issued",    render: (r) => <span className="font-mono text-xs">{fmtDate(r.issued_date)}</span> },
    { key: "due_date",       label: "Due",       render: (r) => <span className="font-mono text-xs">{fmtDate(r.due_date)}</span> },
    { key: "status",         label: "Status",    render: (r) => <StatusPill tone={INVOICE_TONE[r.status] || "zinc"} label={(INVOICE_STATUSES.find((s) => s.k === r.status) || INVOICE_STATUSES[0]).label} /> },
    { key: "file",           label: "File",      render: (r) => <FileCell row={r} token={token} onUpload={files.onUpload} onRemove={files.onRemove} /> },
  ];

  const list = async (params) => {
    const rows = await crmListNonProject(token, params);
    setTotals({
      count: rows.length,
      invoiced: rows.reduce((s, r) => s + (Number(r.total ?? r.amount) || 0), 0),
      received: rows.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0),
      balance: rows.reduce((s, r) => s + (Number(r.balance) || 0), 0),
    });
    return rows;
  };

  const deletePayment = async (pay) => {
    if (!window.confirm(`Remove payment of ${INR_PRECISE(pay.amount)} dated ${fmtDate(pay.date)}?`)) return;
    try {
      const updated = await crmDeleteNonProjectPayment(token, recordingFor.id, pay.id);
      setRecordingFor(updated);
      toast.success("Payment removed");
      refresh();
    } catch (err) { toast.error(apiErrorMsg(err, "Failed to remove payment")); }
  };

  return (
    <div data-testid="crm-non-project-payments-page">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Non-Project Payments</div>
        <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">Additional work invoices</h2>
        <p className="mt-2 text-sm text-[#4a4a4a]">
          Bill clients for extra work outside any project. Each invoice appears on the client&rsquo;s portal under &ldquo;Additional Payments&rdquo; (behind their finance password).
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-6">
        <KpiCard testid="np-kpi-count"    icon={<FileText className="h-3.5 w-3.5" />} label="Invoices" value={totals.count} tone="bg-blue-500" />
        <KpiCard testid="np-kpi-invoiced" icon={<ReceiptIndianRupee className="h-3.5 w-3.5" />} label="Total invoiced" value={INR(totals.invoiced)} tone="bg-indigo-500" />
        <KpiCard testid="np-kpi-received" icon={<Wallet className="h-3.5 w-3.5" />} label="Received" value={INR(totals.received)} tone="bg-emerald-500" accent="text-emerald-700" />
        <KpiCard testid="np-kpi-balance"  icon={<AlertCircle className="h-3.5 w-3.5" />} label="Outstanding" value={INR(totals.balance)} tone="bg-amber-500" accent="text-amber-700" />
      </div>

      <input ref={files.inputRef} type="file" hidden onChange={files.onFileChosen}
        accept="application/pdf,image/png,image/jpeg,image/webp" data-testid="np-invoice-file-input" />

      <CrmPanel
        title="All invoices"
        entityName={ENTITY}
        fields={fields}
        columns={columns}
        list={list}
        create={(p) => crmCreateNonProject(token, coerceGst(p))}
        update={(id, p) => crmUpdateNonProject(token, id, coerceGst(p))}
        remove={(id) => crmDeleteNonProject(token, id)}
        filters={[
          { key: "client_id", label: "Client", options: clientOpts.map((c) => ({ k: c.id, label: c.label })) },
          { key: "status", label: "Status", options: INVOICE_STATUSES },
        ]}
        initialForm={{ gst_applicable: "true", gst_pct: 18, status: "draft", issued_date: new Date().toISOString().slice(0, 10) }}
        emptyText="No additional-work invoices yet. Click + New to bill a client."
      />

      {recordingFor && (
        <RecordPaymentModal
          token={token}
          invoice={recordingFor}
          onRecord={(payload) => crmRecordNonProjectPayment(token, recordingFor.id, payload)}
          onDeletePayment={deletePayment}
          onClose={() => setRecordingFor(null)}
          onSuccess={() => { setRecordingFor(null); refresh(); }}
        />
      )}
    </div>
  );
}
